/* eslint-disable react-refresh/only-export-components */
import clsx from 'clsx'
import { Info } from 'lucide-react'
import type { ReactNode } from 'react'
import type { Figure } from '@/types/dashboards'

/** A figure the server measured (a value, even a zero) vs one with no source. */
export function isMeasured(figure: Figure | null | undefined): boolean {
  if (!figure) return false
  const { value } = figure
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  return Number.isFinite(value)
}

/** Split a figure list into what is on record and what is not — so a panel
 *  draws the known figures with room to read them and states the unknown
 *  ones in one line, instead of a grid of `--` tiles. */
export function splitFigures<T extends Figure>(figures: readonly T[] | null | undefined): {
  known: T[]
  unknown: T[]
} {
  const known: T[] = []
  const unknown: T[] = []
  for (const figure of figures ?? []) (isMeasured(figure) ? known : unknown).push(figure)
  return { known, unknown }
}

/** "Not on record: Production vs plan, Net revenue — no finance close … ·
 *  Risk-adjusted ROE — …" — unknown figures grouped by the basis the server
 *  sent, so a reason shared by three tiles is printed once. */
export function unmeasuredStatement(figures: readonly Figure[], lead = 'Not on record'): string | null {
  if (figures.length === 0) return null
  const groups = new Map<string, string[]>()
  for (const figure of figures) {
    const key = figure.basis?.trim() ?? ''
    const labels = groups.get(key) ?? []
    if (!labels.includes(figure.label)) labels.push(figure.label)
    groups.set(key, labels)
  }
  const parts = [...groups.entries()].map(([basis, labels]) =>
    basis ? `${labels.join(', ')} — ${basis.replace(/\.$/, '')}` : labels.join(', '),
  )
  return `${lead}: ${parts.join(' · ')}.`
}

interface UnmeasuredLineProps {
  figures: readonly Figure[]
  /** Why these have no source, when the panel knows it better than the figures do. */
  reason?: string
  lead?: string
  /** A short complete sentence to print instead of the full list, which then
   *  sits behind the info mark — for a narrow panel with many unknowns. */
  summary?: string
  className?: string
}

/**
 * One precise line in place of empty chrome. Renders nothing when every
 * figure is measured.
 */
export default function UnmeasuredLine({ figures, reason, lead, summary, className }: UnmeasuredLineProps) {
  const statement = unmeasuredStatement(figures, lead)
  if (!statement) return null
  const text = reason ? `${statement} ${reason}` : statement
  if (summary) {
    return (
      <p className={clsx('text-[9.5px] leading-snug text-stone-500', className)} data-testid="unmeasured-line">
        <BasisNote full={text}>{summary}</BasisNote>
      </p>
    )
  }
  return (
    <p className={clsx('text-[9.5px] leading-snug text-stone-500', className)} data-testid="unmeasured-line">
      <span className="mr-1 inline-block h-1 w-1 translate-y-[-1px] rounded-full bg-stone-600 align-middle" aria-hidden="true" />
      {text}
    </p>
  )
}

interface BasisNoteProps {
  /** A short, complete sentence — never cut. */
  children: ReactNode
  /** The full basis, behind an info mark, when it says more than the sentence. */
  full?: string | null
  className?: string
}

/**
 * What a panel stands on, in one short complete sentence that wraps rather
 * than ends in an ellipsis; the server's long form sits behind the info mark.
 */
export function BasisNote({ children, full, className }: BasisNoteProps) {
  return (
    <span className={clsx('inline leading-snug', className)}>
      {children}
      {full ? (
        <span title={full} aria-label={full} className="ml-1 inline-flex translate-y-[1px] cursor-help text-stone-500 hover:text-amber-200">
          <Info size={10} />
        </span>
      ) : null}
    </span>
  )
}
